import { Component, forwardRef, OnInit } from '@angular/core';
import { Validators } from '@angular/forms';
import { PrFormAbstract } from '@app/components/pr-form/pr-form.abstract';
import { PrFormConstructor } from '@app/components/pr-form/pr-form.contructor';
import { AssetDao } from '@app/dao/asset/asset.dao';
import { UserDao } from '@app/dao/user/user.dao';
import { BehaviorSubject, combineLatest, Subject, Subscription, switchMap } from 'rxjs';
import { PrDefiscFormBuilder } from './ea-defisc-form.builder';
import { AssetTypeService } from '@app/services/asset-type.service';

@Component({
    selector: 'ea-defisc-form',
    templateUrl: './ea-defisc-form.component.html',
    styleUrls: ['./ea-defisc-form.component.scss'],
    providers: [
        {
            provide: PrFormAbstract,
            useExisting: forwardRef(() => EaDefiscFormComponent),
        },
    ],
    standalone: false
})
export class EaDefiscFormComponent
  extends PrDefiscFormBuilder
  implements OnInit
{
  users: any[];
  references: any;
  groupes: any[];
  sources: any[];
  managers: any[];
  doneSubject: Subject<void> = new Subject<void>();
  tabChanged: BehaviorSubject<any> = new BehaviorSubject(null);
  changeTab: BehaviorSubject<any> = new BehaviorSubject(null);

  products = this.assetTypeService.getTaxExemptingAssetTypeOptions();

  soficaDefiscalisations: any[] = [
    { id: 'RATE_30', name: '30%' },
    { id: 'RATE_36', name: '36%' },
    { id: 'RATE_48', name: '48%' },
  ];

  private SubscriptionType: Subscription;
  constructor(
    protected formConstructor: PrFormConstructor,
    protected assetTypeService: AssetTypeService,
    private userDao: UserDao,
    private assetDao: AssetDao,
  ) {
    super(formConstructor);
  }

  ngOnInit() {
    combineLatest([
      this.userDao.getCouple(),
      this.assetDao.getReferences(),
      this.assetDao.getProviderByType(this.entity.type),
    ]).subscribe(([users, references, managers]) => {
      this.users = users;
      this.references = references;
      this.sources = references[0];
      this.managers = managers;
      this.SubscriptionType = this.form
        .get('type')
        .valueChanges.subscribe(value => {
          if (value === 'GIRARDIN') {
            this.form.get('girardinRate').setValidators([Validators.required]);
          } else {
            this.form.get('girardinRate').clearValidators();
          }
          if (
            [
              'SOFICA',
              ...this.assetTypeService.getTaxExemptingAssetTypeFipFCPI(),
            ].includes(value)
          ) {
            this.form
              .get('taxReductionRate')
              .setValidators([Validators.required]);
          } else {
            this.form.get('taxReductionRate').clearValidators();
          }
          this.form.get('girardinRate').updateValueAndValidity();
          this.form.get('taxReductionRate').updateValueAndValidity();
        });
      if (!this.form.get('userId').value)
        this.form.get('userId').setValue(users[0].id);
      if (!this.form.get('accountId').value)
        this.form.get('accountId').setValue(users[0].accountId);
    });
  }

  get title() {
    let type =
      this.entity && this.entity.type
        ? this.entity.type.toLocaleLowerCase()
        : '';
    switch (type) {
      case 'girardin':
        return this.generateTitle(
          "Modifier l'opération",
          'Ajout d’une opération Girardin',
        );
      case 'fcpi':
        return this.generateTitle(
          "Modifier l'opération",
          'Ajout d’une opération FCPI',
        );
      case 'fip':
        return this.generateTitle(
          "Modifier l'opération",
          'Ajout d’une opération FIP',
        );
      case 'fip/dom-tom':
        return this.generateTitle(
          "Modifier l'opération",
          'Ajout d’une opération FIP DOM TOM',
        );
      case 'fip/corse':
        return this.generateTitle(
          "Modifier l'opération",
          'Ajout d’une opération FIP Corse',
        );
      case 'sofica':
        return this.generateTitle(
          "Modifier l'opération",
          'Ajout d’une opération SOFICA',
        );
      case 'crowndfunding':
        return this.generateTitle(
          "Modifier l'opération",
          'Ajout d’une opération de crowndfunding',
        );
      case 'pme':
        return this.generateTitle(
          "Modifier l'opération",
          'Ajout d’un investissement private equity',
        );
      case 'tangibles':
        return this.generateTitle(
          "Modifier l'opération",
          'Ajout d’un actif tangible',
        );
      default:
        return this.generateTitle(
          "Modifier l'opération",
          "Ajout d'un produit de défiscalisation",
        );
    }
  }

  generateTitle(editTitle: string, addTitle: string) {
    if (!this.entity || !this.entity.id) return addTitle;
    if (!this.entity.label || this.entity.label === '') return editTitle;
    return `${editTitle} “${this.entity.label}”`;
  }

  ///REFACTO
  selectType(type) {
    this.form.controls.type.setValue(type);
    this.form.updateValueAndValidity();
    this.assetDao.getProviderByType(type).subscribe(res => {
      this.managers = res;
    });
  }

  onSubmit() {
    this.displayErrors();

    this.selectErrorInput();

    if (this.form.invalid) {
      this.changeTab.next('FIRST');
      return;
    }
    //Fetch every form controls value
    let formDTOExtraction = this.extract();
    this.prFormService.loading();
    this.assetDao
      .proposeManagerProvider(formDTOExtraction, this.managers)
      .pipe(switchMap(() => this.assetDao.saveAssets(formDTOExtraction)))
      .subscribe(
        success => {
          this.prFormService.loaded();
          this.doneSubject.next();
          if (this.callback) this.callback(success);
          this.notificationService.success('La sauvegarde a réussi');
        },
        errors => {
          console.error(errors);
          this.prFormService.loaded();
          this.notificationService.error('La sauvegarde a échoué');
        },
      );
  }

  onCancel() {
    this.prFormService.close();
  }

  onTabChange(isLastTab: boolean) {
    this.tabChanged.next(isLastTab);
  }

  nextTab() {
    this.changeTab.next('NEXT');
  }

  ngOnDestroy() {
    if (this.SubscriptionType) {
      this.SubscriptionType.unsubscribe();
    }
  }
}
